import { Vendor } from '../types/vendor';
import { getVendorRating, getJobsCompleted } from './vendorHelpers';

/**
 * Vendor sorting utilities
 */

export type SortOption = 'name' | 'rating' | 'jobs' | 'date';
export type SortDirection = 'asc' | 'desc';

/**
 * Sort vendors by the specified field and direction
 */
export function sortVendors(
  vendors: Vendor[],
  sortBy: SortOption,
  direction: SortDirection = 'asc'
): Vendor[] {
  const sorted = [...vendors].sort((a, b) => {
    let compareValue = 0;

    switch (sortBy) {
      case 'name':
        compareValue = a.name.localeCompare(b.name);
        break;

      case 'rating': {
        const ratingA = getVendorRating(a) || 0;
        const ratingB = getVendorRating(b) || 0;
        compareValue = ratingA - ratingB;
        break;
      }

      case 'jobs': {
        const jobsA = getJobsCompleted(a) || 0;
        const jobsB = getJobsCompleted(b) || 0;
        compareValue = jobsA - jobsB;
        break;
      }

      case 'date': {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        compareValue = dateA - dateB;
        break;
      }

      default:
        compareValue = 0;
    }

    return direction === 'asc' ? compareValue : -compareValue;
  });

  return sorted;
}

/**
 * Paginate an array of vendors
 */
export function paginateVendors(
  vendors: Vendor[],
  page: number,
  pageSize: number = 20
): { vendors: Vendor[]; totalPages: number; hasMore: boolean } {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedVendors = vendors.slice(startIndex, endIndex);
  const totalPages = Math.ceil(vendors.length / pageSize);
  const hasMore = page < totalPages;

  return {
    vendors: paginatedVendors,
    totalPages,
    hasMore,
  };
}

/**
 * Export vendors to CSV format
 */
export function exportVendorsToCSV(vendors: Vendor[]): string {
  const headers = [
    'Name',
    'DBA Name',
    'Contact Name',
    'Email',
    'Phone',
    'Status',
    'Services',
    'Jobs Completed',
    'Rating',
    'On-Time Rate',
    'Customer Satisfaction',
    'Total Revenue',
    'Created Date',
    'Approved Date',
  ];

  const rows = vendors.map(vendor => {
    const services = (vendor.services ?? []).join('; ');
    const jobsCompleted = getJobsCompleted(vendor);
    const rating = getVendorRating(vendor);
    
    return [
      vendor.name,
      vendor.dbaName || '',
      vendor.contactName,
      vendor.email,
      vendor.phone,
      vendor.status,
      services,
      jobsCompleted.toString(),
      rating ? rating.toFixed(1) : '',
      vendor.performanceMetrics?.onTimeCompletionRate?.toString() || '',
      vendor.performanceMetrics?.customerSatisfaction?.toString() || '',
      vendor.performanceMetrics?.totalRevenue?.toString() || '',
      new Date(vendor.createdAt).toLocaleDateString(),
      vendor.approvedAt ? new Date(vendor.approvedAt).toLocaleDateString() : '',
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string = 'vendors.csv'): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
